<script lang="ts">
	// The item sheet on a restaurant's page (UC-31), #959's option A: a tap on
	// an item that opens shows the whole item in a sheet over the menu, which
	// keeps its scroll and its filter. On a phone the sheet rises from the
	// foot, at most --sheet-bottom-max high, with «اقفل» across its foot and a
	// handle that closes it on a tap; from 640px it is a centred panel as high
	// as its content, the photo, the name and the price in one column and the
	// description and the groups in the other. The scrim, Esc, «اقفل», the
	// handle and the phone's Back all close it.
	//
	// It fetches nothing and gets no item as data: the worker drew each item's
	// body inside its row's <details> (MenuItem.svelte), and the sheet moves
	// that markup in while it is open and back when it closes. A description
	// longer than three lines shows three, then «اقرا أكتر».

	import { tick } from 'svelte';
	import Button from '../components/Button.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { restaurant } from './copy';

	// The item whose body the sheet shows; it stays set while the sheet leaves.
	let shown = $state<HTMLDetailsElement>();
	let open = $state(false);
	let folds = $state(false);
	let expanded = $state(false);

	const key = $derived(shown?.dataset.item ?? '');

	// One listener for the whole menu: a tap on an item's row opens the
	// sheet instead of opening the item in place.
	$effect(() => {
		const menu = document.querySelector<HTMLElement>('[data-menu]');
		if (!menu) return;
		const onClick = (event: MouseEvent) => {
			const summary = (event.target as Element | null)?.closest('summary');
			const item = summary?.parentElement;
			if (!(item instanceof HTMLDetailsElement) || !item.dataset.item || !menu.contains(item))
				return;
			event.preventDefault();
			item.open = false;
			shown = item;
			expanded = false;
			open = true;
		};
		menu.addEventListener('click', onClick);
		return () => menu.removeEventListener('click', onClick);
	});

	let description = $state<HTMLElement>();

	/**
	 * Moves one part of the shown item's body into the sheet, at the start of
	 * its slot, and back to its place when the sheet lets it go.
	 */
	function take(part: string) {
		return (slot: HTMLElement) => {
			const body = shown?.querySelector<HTMLElement>('[data-item-body]');
			const node = body?.querySelector<HTMLElement>(`:scope > [${part}]`);
			if (!body || !node) return;
			const after = node.nextSibling;
			slot.prepend(node);
			if (part === 'data-item-description') fold(node);
			return () => {
				node.removeAttribute('data-clamp');
				if (node === description) description = undefined;
				body.insertBefore(node, after && after.parentNode === body ? after : null);
			};
		};
	}

	// A description folds to three lines only when it is longer than three.
	function fold(node: HTMLElement) {
		description = node;
		folds = false;
		node.setAttribute('data-clamp', '');
		void tick().then(() => {
			folds = node.scrollHeight > node.clientHeight + 1;
			if (!folds) node.removeAttribute('data-clamp');
		});
	}

	function readMore() {
		expanded = true;
		description?.removeAttribute('data-clamp');
	}
</script>

<Sheet
	{open}
	title=""
	labelledBy="item-{key}"
	placement="auto"
	fit
	handle
	history
	closeLabel={restaurant.close}
	onClose={() => (open = false)}
	testId="catalogue-item-sheet"
	closeTestId="catalogue-item-close"
	handleTestId="catalogue-item-handle"
>
	<div class="wrap">
		<div class="body">
			<div class="lead" {@attach take('data-item-lead')}></div>
			<div class="rest">
				<div class="description" {@attach take('data-item-description')}>
					{#if folds && !expanded}
						<Button
							testId="catalogue-item-more"
							tone="text-quiet"
							size="sm"
							label={restaurant.readMore}
							trailingIcon="expand"
							onPress={readMore}
						/>
					{/if}
				</div>
				<div {@attach take('data-item-groups')}></div>
			</div>
		</div>
	</div>
</Sheet>

<style>
	.wrap {
		container-type: inline-size;
	}

	.body {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.lead {
		display: flex;
		flex-direction: column;
		min-inline-size: 0;
	}

	.rest {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
		min-inline-size: 0;
	}

	.description {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}

	.description:empty,
	.rest > :empty {
		display: none;
	}

	/* From a 720px body, a photo heads the start column and the rest takes
	   the other; with no photo the name leads, in one column. */
	@container (min-width: 720px) {
		.body:has(:global([data-item-photo])) {
			display: grid;
			grid-template-columns: var(--photo-lg) minmax(0, 1fr);
			column-gap: var(--space-9);
			align-items: start;
		}
	}
</style>
